import Vue from 'vue';
import Router from 'vue-router';
import { loadSites, useSiteProvider } from '@/helpers/site';
import { usePermissionProvider, useUserProvider } from '@/helpers/user';
import { ADMIN_ROLE_ID, permissionTypes, SENIOR_EDITOR_ROLE_ID } from '@/components/user/constants';
import { acquireIdToken } from '@/helpers/misc/auth';
import { findInNestedArray } from '@/helpers/misc';
import { loadUser } from '@/helpers/user/useUserProvider';
import { loadPermissions } from '@/helpers/user/usePermissionProvider';

Vue.use(Router);

const { setAuthUser, currentUserRoles } = useUserProvider();
const { isGranted, hasSiteAccess } = usePermissionProvider();

export const hasAnyRole = (allowedRoleIds = [], userRoleIds = []) => (userRoleIds || [])
  .some(roleId => allowedRoleIds.includes(roleId?.toString()));

export const contentTypeRoutes = [
  {
    path: 'news/:id?',
    name: 'news',
    component: () => import('@/modules/content/news/NewsDetailPage.vue'),
    meta: {
      icon: 'mdi-newspaper',
      hideSideMenu: true,
      permission: permissionTypes.news.view,
    },
  },
  {
    path: 'video/:id?',
    name: 'video',
    component: () => import('@/modules/content/video/VideoDetailPage.vue'),
    meta: {
      icon: 'mdi-video',
      hideSideMenu: true,
      permission: permissionTypes.video.view,
    },
  },
  {
    path: 'photo-gallery/:id?',
    name: 'photo-gallery',
    component: () => import('@/modules/content/photo-gallery/PhotoGalleryDetailPage.vue'),
    meta: {
      icon: 'mdi-image-multiple',
      hideSideMenu: true,
      permission: permissionTypes.photoGallery.view,
    },
  },
  {
    path: 'column/:id?',
    name: 'column',
    component: () => import('@/modules/content/column/ColumnDetailPage.vue'),
    meta: {
      icon: 'mdi-text',
      hideSideMenu: true,
      permission: permissionTypes.column.view,
    },
  },
  {
    path: 'cartoon/:id?',
    name: 'cartoon',
    component: () => import('@/modules/content/cartoon/CartoonDetailPage.vue'),
    meta: {
      icon: 'mdi-pencil',
      hideSideMenu: true,
      permission: permissionTypes.cartoon.view,
    },
  },
  {
    path: 'infographic/:id?',
    name: 'infographic',
    component: () => import('@/modules/content/infographic/InfographicDetailPage.vue'),
    meta: {
      icon: 'mdi-chart-pie',
      hideSideMenu: true,
      permission: permissionTypes.infographic.view,
    },
  },
  {
    path: 'promotion/:id?',
    name: 'promotion',
    component: () => import('@/modules/content/promotion/PromotionDetailPage.vue'),
    meta: {
      icon: 'mdi-billboard',
      hideSideMenu: true,
      permission: permissionTypes.promotion.view,
    },
  },
  {
    path: 'podcast/:id?',
    name: 'podcast',
    component: () => import('@/modules/content/podcast/PodcastDetailPage.vue'),
    meta: {
      icon: 'mdi-podcast',
      hideSideMenu: true,
      permission: permissionTypes.podcast.view,
    },
  },
  {
    path: 'official-announcement/:id?',
    name: 'official-announcement',
    component: () => import('@/modules/content/official-announcement/OfficialAnnouncementDetailPage.vue'),
    meta: {
      icon: 'mdi-file-document',
      hideSideMenu: true,
      permission: permissionTypes.officialAnnouncement.view,
    },
  },
  {
    path: 'newspaper/:id?',
    name: 'newspaper',
    component: () => import('@/modules/content/newspaper/NewspaperDetailPage.vue'),
    meta: {
      icon: 'mdi-newspaper',
      hideSideMenu: true,
      permission: permissionTypes.newspaper.view,
    },
  }
];

export const siteRoutes = [
  {
    path: 'dashboard',
    name: 'dashboard',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'mdi-view-dashboard',
      redirectFirstChild: true,
      showInMenu: true,
      // The dashboard is not gated behind a feature permission: anyone who can work on
      // a site can see how that site is doing. Access still ends at the site boundary,
      // which `requiresSiteAccess` enforces.
      requiresSiteAccess: true,
    },
    children: [
      {
        path: 'overview',
        name: 'dashboard-overview',
        component: () => import('@/modules/dashboard/DashboardPage.vue'),
        meta: {
          requiresSiteAccess: true,
        },
      },
      {
        path: 'editor/:userId',
        name: 'dashboard-editor-detail',
        component: () => import('@/modules/dashboard/EditorPerformanceDetailPage.vue'),
        meta: {
          hideSideMenu: true,
          requiresSiteAccess: true,
        },
      },
    ],
  },
  {
    path: 'agency',
    name: 'agency',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'mdi-rss',
      redirectFirstChild: true,
      showInMenu: true,
      permission: permissionTypes.agency.view,
    },
    children: [
      {
        path: 'list/:tab?',
        name: 'agency-list',
        component: () => import('@/modules/agency/AgencyListPage.vue'),
        meta: {
          permission: permissionTypes.agency.view,
        },
      },
    ],
  },
  {
    path: 'archive',
    name: 'archive',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'collections_bookmark',
      redirectFirstChild: true,
      showInMenu: true,
      permission: permissionTypes.agencyArchive.view,
    },
    children: [
      {
        path: 'list',
        name: 'archive-list',
        component: () => import('@/modules/agency/AgencyArchiveListPage.vue'),
        meta: {
          permission: permissionTypes.agencyArchive.view,
        },
      }
    ],
  },
  {
    path: 'content',
    name: 'content',
    component: () => import('@/layouts/SideMenu.vue'),
    meta: {
      icon: 'mdi-view-list',
      redirectFirstChild: true,
      showInMenu: true,
      hideSideMenuForSiteIds: ['30'],
      permission: [permissionTypes.content.view,
        permissionTypes.news.view,
        permissionTypes.video.view,
        permissionTypes.photoGallery.view,
        permissionTypes.column.view,
        permissionTypes.cartoon.view,
        permissionTypes.infographic.view,
        permissionTypes.promotion.view,
        permissionTypes.podcast.view,
        permissionTypes.officialAnnouncement.view,
        permissionTypes.notification.view,
        permissionTypes.liveTransfer.view,
        permissionTypes.videoProcessQueue.view,
        permissionTypes.editorContentReport.view,
        permissionTypes.comment.view],
    },
    children: [
      {
        path: 'list/:type?',
        name: 'content-list',
        component: () => import('@/modules/content/ContentListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.content.view,
        },
      },
      {
        path: 'list-draft/:type?',
        name: 'content-list-draft',
        component: () => import('@/modules/content/DraftContentListPage.vue'),
        meta: {
          showInMenu: false,
          permission: permissionTypes.content.view,
        },
      },
      ...contentTypeRoutes,

      {
        path: 'comments',
        name: 'comments-list',
        component: () => import('@/modules/comments/RegularCommentsListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.comment.view,
        },
      },
      {
        path: 'reported-comments',
        name: 'reported-comments-list',
        component: () => import('@/modules/comments/ReportedCommentsListPage.vue'),
        meta: {
          showInMenu: false,
          permission: permissionTypes.comment.view,
        },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/modules/content/notification/NotificationListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.notification.view,
        },
      },
      {
        path: 'live-transfers',
        name: 'live-transfers',
        component: () => import('@/modules/content/sports/LiveTransferListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.liveTransfer.view,
        },
      },
      {
        path: 'video-process-queue',
        name: 'video-process-queue',
        component: () => import('@/modules/content/video-process-queue/VideoProcessQueueListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.videoProcessQueue.view,
        },
      },
      {
        path: 'editor-content-report',
        name: 'editor-content-report',
        component: () => import('@/modules/content/editorContentReport/EditorContentReportListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.editorContentReport.view,
        },
      },
      {
        path: 'ai-content',
        name: 'ai-content-list',
        component: () => import('@/modules/ai-content/AiContentListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.content.view,
        },
      },
    ],
  },
  {
    path: 'headline',
    name: 'headline',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'move_up',
      showInMenu: true,
      redirectFirstChild: true,
      permission: permissionTypes.headline.view,
    },
    children: [
      {
        path: 'list',
        name: 'headline-list',
        component: () => import('@/modules/headline/HeadlineListPage.vue'),
        meta: {
          permission: permissionTypes.headline.view,
        },
      },
    ],
  },
  {
    path: 'headline-old',
    name: 'headline-old',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'restaurant',
      showInMenu: true,
      redirectFirstChild: true,
      permission: permissionTypes.headlineOld.view,
    },
    children: [
      {
        path: 'list',
        name: 'headlines-old-list',
        component: () => import('@/modules/headline/old/HeadlineListPage.vue'),
        meta: {
          permission: permissionTypes.headlineOld.view,
        },
      },
    ],
  },
  {
    path: 'seo',
    name: 'seo',
    component: () => import('@/layouts/SideMenu.vue'),
    meta: {
      icon: 'query_stats',
      redirectFirstChild: true,
      showInMenu: true,
      hasSubMenu: false,
      permission: permissionTypes.seoContents.view,
    },
    children: [
      {
        path: 'hadith',
        name: 'hadith',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          showInMenu: true,
          redirectFirstChild: true,
          permission: permissionTypes.seoContents.view,
        },
        children: [
          {
            path: 'list',
            name: 'hadith-list',
            component: () => import('@/modules/seo/hadith/HadithListPage.vue'),
            meta: {
              showInMenu: false,
              permission: permissionTypes.seoContents.view,
            },
          },
          {
            path: 'detail/:id',
            name: 'hadith-detail',
            component: () => import('@/modules/seo/hadith/HadithDetailPage.vue'),
            meta: {
              showInMenu: false,
              hideSideMenu: true,
              permission: permissionTypes.seoContents.view,
            },
          }
        ],
      },
      {
        path: 'dream-interpretation',
        name: 'dream-interpretation',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.seoContents.view,
        },
        children: [
          {
            path: 'list',
            name: 'dream-interpretation-list',
            component: () => import('@/modules/seo/dream-interpretation/DreamInterpretationListPage.vue'),
            meta: {
              showInMenu: false,
              permission: permissionTypes.seoContents.view,
            },
          },
          {
            path: 'detail/:id?',
            name: 'dream-interpretation-detail',
            component: () => import('@/modules/seo/dream-interpretation/DreamInterpretationDetailPage.vue'),
            meta: {
              showInMenu: false,
              hideSideMenu: true,
              permission: permissionTypes.seoContents.view,
            },
          },
        ],
      },
      {
        path: 'dua',
        name: 'dua',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.seoContents.view,
        },
        children: [
          {
            path: 'list',
            name: 'dua-list',
            component: () => import('@/modules/seo/dua/DuaListPage.vue'),
            meta: {
              showInMenu: false,
              permission: permissionTypes.seoContents.view,
            },
          },
          {
            path: 'detail/:id?',
            name: 'dua-detail',
            component: () => import('@/modules/seo/dua/DuaDetailPage.vue'),
            meta: {
              showInMenu: false,
              hideSideMenu: true,
              permission: permissionTypes.seoContents.view,
            },
          },
        ],
      },
      {
        path: 'fatwa',
        name: 'fatwa',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.seoContents.view,
        },
        children: [
          {
            path: 'list',
            name: 'fatwa-list',
            component: () => import('@/modules/seo/fatwa/FatwaListPage.vue'),
            meta: {
              showInMenu: false,
              permission: permissionTypes.seoContents.view,
            },
          },
          {
            path: 'detail/:id?',
            name: 'fatwa-detail',
            component: () => import('@/modules/seo/fatwa/FatwaDetailPage.vue'),
            meta: {
              showInMenu: false,
              hideSideMenu: true,
              permission: permissionTypes.seoContents.view,
            },
          },
        ],
      },
    ],
  },
  {
    path: 'reels',
    name: 'reels',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'movie',
      redirectFirstChild: true,
      showInMenu: true,
      permission: permissionTypes.reels.view,
    },
    children: [
      {
        path: 'list',
        name: 'reels-list',
        component: () => import('@/modules/reels/ReelsListPage.vue'),
        meta: {
          permission: permissionTypes.reels.view,
        },
      },
      {
        path: 'detail/:id?',
        name: 'reels-detail',
        component: () => import('@/modules/reels/ReelsDetailPage.vue'),
        meta: {
          permission: permissionTypes.reels.view,
        },
      },
      {
        path: 'pending-detail/:id?',
        name: 'reels-pending-detail',
        component: () => import('@/modules/reels/PendingReelsDetailPage.vue'),
        meta: {
          permission: permissionTypes.reels.view,
        },
      },
    ],
  },
  {
    path: 'newspaper-headline',
    name: 'newspaper-headline',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'mdi-newspaper-variant-multiple',
      redirectFirstChild: true,
      showInMenu: true,
      permission: permissionTypes.newspaperHeadline.view,
    },
    children: [
      {
        path: 'list/:date?',
        name: 'newspaper-headline-list',
        component: () => import('@/modules/newspaper-headline/NewspaperHeadlinePage.vue'),
        meta: {
          permission: permissionTypes.newspaperHeadline.view,
        },
      },
      {
        path: 'detail/:newspaperId/:date',
        name: 'newspaper-headline-detail',
        component: () => import('@/modules/newspaper-headline/NewspaperIssueDetailPage.vue'),
        meta: {
          hideSideMenu: true,
          permission: permissionTypes.newspaperHeadline.view,
        },
      },
    ],
  },
  {
    path: 'story',
    name: 'story',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'movie',
      redirectFirstChild: true,
      showInMenu: true,
      permission: permissionTypes.story.view,
    },
    children: [
      {
        path: 'list',
        name: 'story-list',
        component: () => import('@/modules/story/StoryListPage.vue'),
        meta: {
          permission: permissionTypes.story.view,
        },
      },
      {
        path: 'detail/:id?',
        name: 'story-detail',
        component: () => import('@/modules/story/StoryPage.vue'),
        meta: {
          permission: permissionTypes.story.view,
        },
      },
      {
        path: 'pending-detail/:id?',
        name: 'story-pending-detail',
        component: () => import('@/modules/story/PendingStoryPage.vue'),
        meta: {
          permission: permissionTypes.story.view,
        },
      }
    ],
  },
  {
    path: 'recipe',
    name: 'recipe',
    component: () => import('@/layouts/SideMenu.vue'),
    meta: {
      icon: 'mdi-silverware-fork-knife',
      redirectFirstChild: true,
      showInMenu: true,
      hasSubMenu: true,
      permission: permissionTypes.recipe.view,
    },
    children: [
      {
        path: 'list',
        name: 'recipe-list',
        component: () => import('@/modules/recipe/RecipeListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
      },
      {
        path: 'list-draft',
        name: 'recipe-list-draft',
        component: () => import('@/modules/recipe/RecipeDraftListPage.vue'),
        meta: {
          showInMenu: false,
          permission: permissionTypes.recipe.view,
        },
      },
      {
        path: 'notifications',
        name: 'recipe-notifications',
        component: () => import('@/modules/content/notification/NotificationListPage.vue'),
        props: { mediumId: 2 },
        meta: {
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
      },
      {
        path: 'daily-menu',
        name: 'recipe-daily-menu',
        component: () => import('@/modules/recipe/daily-menu/DailyMenuListPage.vue'),
        meta: {
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
      },
      {
        path: 'detail/:id?',
        name: 'recipe-detail',
        component: () => import('@/modules/recipe/RecipeDetailPage.vue'),
        meta: {
          showInMenu: false,
          hideSideMenu: true,
          permission: permissionTypes.recipe.view,
        },
      },
      {
        path: 'subfields',
        name: 'recipe-ingredients',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'ingredients/list',
            name: 'recipe-ingredient-list',
            component: () => import('@/modules/recipe/ingredient/RecipeIngredientListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
            },
          },
          {
            path: 'ingredient-types/list',
            name: 'recipe-ingredient-types-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'ingredientTypes' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'ingredientTypes',
            },
          },
          {
            path: 'rayons/list',
            name: 'recipe-rayons-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'rayons' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'rayons',
            },
          },
          {
            path: 'brands/list',
            name: 'recipe-brand-list',
            component: () => import('@/modules/recipe/brand/RecipeBrandListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
            },
          },
        ],
      },
      {
        path: 'cuisines',
        name: 'recipe-cuisines',
        component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataLayout.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'list',
            name: 'recipe-cuisines-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'cuisines' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'cuisines',
            },
          },
        ],
      },
      {
        path: 'meal-types',
        name: 'recipe-meal-types',
        component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataLayout.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'list',
            name: 'recipe-meal-types-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'mealTypes' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'mealTypes',
            },
          },
        ],
      },
      {
        path: 'measurements',
        name: 'recipe-measurements',
        component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataLayout.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'list',
            name: 'recipe-measurements-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'measurements' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'measurements',
            },
          },
        ],
      },
      {
        path: 'utensils',
        name: 'recipe-utensils',
        component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataLayout.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'list',
            name: 'recipe-utensils-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'utensils' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'utensils',
            },
          },
        ],
      },
      {
        path: 'healthy-lives',
        name: 'recipe-healthy-lives',
        component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataLayout.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'list',
            name: 'recipe-healthy-lives-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'healthyLives' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'healthyLives',
            },
          },
        ],
      },
      {
        path: 'steps',
        name: 'recipe-steps',
        component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataLayout.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: permissionTypes.recipe.view,
        },
        children: [
          {
            path: 'list',
            name: 'recipe-steps-list',
            component: () => import('@/modules/recipe/reference-data/RecipeReferenceDataListPage.vue'),
            props: { entityType: 'steps' },
            meta: {
              showInMenu: true,
              permission: permissionTypes.recipe.view,
              entityType: 'steps',
            },
          },
        ],
      }
    ],
  },
  {
    path: 'mailing',
    name: 'mailing',
    component: () => import('@/layouts/Default.vue'),
    meta: {
      icon: 'mail',
      redirectFirstChild: true,
      showInMenu: true,
      permission: [permissionTypes.mailing.view,
        permissionTypes.mailingOld.view],
    },
    children: [
      {
        path: 'list',
        name: 'mailing-list',
        component: () => import('@/modules/mailing/MailingListPage.vue'),
        meta: {
          permission: permissionTypes.mailing.view,
        },
      },
      {
        path: 'detail/:id?',
        name: 'mailing-detail',
        component: () => import('@/components/mailing/AddOrEditCustomMailingPage.vue'),
        meta: {
          permission: permissionTypes.mailing.view,
        },
      },
      {
        path: 'old/:sendDate?',
        name: 'mailing-old',
        component: () => import('@/modules/mailing/old/MailingPage.vue'),
        meta: {
          permission: permissionTypes.mailingOld.view,
        },
      },
      {
        path: 'old-upcoming',
        name: 'mailing-old-upcoming',
        component: () => import('@/modules/mailing/old/MailingUpcomingPage.vue'),
        meta: {
          permission: permissionTypes.mailingOld.view,
        },
      },
      {
        path: 'old-sent',
        name: 'mailing-old-sent',
        component: () => import('@/modules/mailing/old/MailingSentPage.vue'),
        meta: {
          permission: permissionTypes.mailingOld.view,
        },
      },
    ],
  },
  {
    path: 'settings',
    name: 'settings',
    component: () => import('@/layouts/SideMenu.vue'),
    meta: {
      icon: 'settings',
      redirectFirstChild: true,
      showInMenu: true,
      hasSubMenu: true,
      permission: [permissionTypes.category.view,
        permissionTypes.signature.view,
        permissionTypes.chef.view,
        permissionTypes.source.view,
        permissionTypes.author.view,
        permissionTypes.cartoonist.view,
        permissionTypes.program.view,
        permissionTypes.headlineTag.view,
        permissionTypes.headlineSettings.view,
        permissionTypes.menu.view,
        permissionTypes.brand.view,
        permissionTypes.mailing.view,
        permissionTypes.user.view,
        permissionTypes.featureFlags.view,
        permissionTypes.turboAdvertisement.view],
    },
    children: [
      {
        path: 'content',
        name: 'content-settings',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          redirectFirstChild: true,
          showInMenu: true,
          permission: [permissionTypes.category.view,
            permissionTypes.signature.view,
            permissionTypes.chef.view,
            permissionTypes.source.view,
            permissionTypes.author.view,
            permissionTypes.cartoonist.view,
            permissionTypes.program.view],
        },
        children: [
          {
            path: 'categories/:id?',
            name: 'categories',
            component: () => import('@/modules/settings/category/CategoryListPage.vue'),
            meta: {
              permission: permissionTypes.category.view,
              showInMenu: true,
            },
          },
          {
            path: 'seo',
            name: 'seo-settings',
            component: () => import('@/modules/settings/seo/SeoListPage.vue'),
            meta: {
              requiresSiteAccess: true,
              roles: [ADMIN_ROLE_ID, SENIOR_EDITOR_ROLE_ID],
              showInMenu: true,
            },
          },
          {
            // Which tournaments the sport pages of the site show (`configs` -> tournamentId).
            path: 'tournaments',
            name: 'tournaments',
            component: () => import('@/modules/settings/tournament/TournamentSettingsPage.vue'),
            meta: {
              requiresSiteAccess: true,
              roles: [ADMIN_ROLE_ID, SENIOR_EDITOR_ROLE_ID],
              showInMenu: true,
            },
          },
          {
            path: 'signatures',
            name: 'signatures',
            component: () => import('@/modules/settings/signature/SignatureListPage.vue'),
            meta: {
              permission: permissionTypes.signature.view,
              showInMenu: true,
            },
          },
          {
            path: 'chefs',
            name: 'chefs',
            component: () => import('@/modules/settings/chef/ChefListPage.vue'),
            meta: {
              permission: permissionTypes.chef.view,
              showInMenu: true,
            },
          },
          {
            path: 'sources',
            name: 'sources',
            component: () => import('@/modules/settings/source/SourceListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.source.view,
            },
          },
          {
            path: 'authors',
            name: 'authors',
            component: () => import('@/modules/settings/author/AuthorListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.author.view,
            },
          },
          {
            path: 'cartoonists',
            name: 'cartoonists',
            component: () => import('@/modules/settings/cartoonist/CartoonistListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.cartoonist.view,
            },
          },
          {
            path: 'programs',
            name: 'programs',
            component: () => import('@/modules/settings/program/ProgramListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.program.view,
            },
          },
        ],
      },
      {
        path: 'headline',
        name: 'headline-settings',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          showInMenu: true,
          redirectFirstChild: true,
          permission: [permissionTypes.headlineTag.view,
            permissionTypes.headlineSettings.view],
        },
        children: [
          {
            path: 'headline-tags',
            name: 'headline-tags',
            component: () => import('@/modules/settings/headline-tag/HeadlineTagListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.headlineTag.view,
            },
          },
          {
            path: 'headline-types',
            name: 'headline-types',
            component: () => import('@/modules/settings/headline-type/HeadlineTypeListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.headlineSettings.view,
            },
          },
          {
            path: 'headline-categories',
            name: 'headline-categories',
            component: () => import('@/modules/settings/headline-category/HeadlineCategoryListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.headlineSettings.view,
            },
          },
        ],
      },
      {
        path: 'other',
        name: 'other-settings',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          showInMenu: true,
          redirectFirstChild: true,
          permission: [permissionTypes.menu.view,
            permissionTypes.brand.view,
            permissionTypes.mailing.view],
        },
        children: [
          {
            path: 'menu',
            name: 'menu',
            component: () => import('@/layouts/Empty.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.menu.view,
              redirectFirstChild: true,
            },
            children: [
              {
                path: 'list',
                name: 'menus',
                component: () => import('@/modules/settings/menu/MenuListPage.vue'),
                meta: {
                  showInMenu: false,
                  permission: permissionTypes.menu.view,
                },
              },
              {
                path: 'detail/:id?',
                name: 'menu-detail',
                component: () => import('@/modules/settings/menu/MenuDetailPage.vue'),
                meta: {
                  showInMenu: false,
                  permission: permissionTypes.menu.view,
                },
              },
            ],
          },
          {
            path: 'brands',
            name: 'brands',
            component: () => import('@/modules/settings/brand/BrandListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.brand.view,
            },
          },
          {
            path: 'mailing-categories',
            name: 'mailing-categories',
            component: () => import('@/modules/settings/mailing-category/MailingCategoryListPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.mailing.view,
            },
          },
        ],
      },
      {
        path: 'system',
        name: 'system-settings',
        component: () => import('@/layouts/Empty.vue'),
        meta: {
          showInMenu: true,
          redirectFirstChild: true,
          permission: [permissionTypes.user.view,
            permissionTypes.featureFlags.view,
            permissionTypes.turboAdvertisement.view],
        },
        children: [
          {
            path: 'user',
            name: 'user',
            component: () => import('@/layouts/Empty.vue'),
            meta: {
              showInMenu: true,
              redirectFirstChild: true,
              permission: permissionTypes.user.view,
            },
            children: [
              {
                path: 'list',
                name: 'user-list',
                component: () => import('@/modules/settings/user/UserListPage.vue'),
                meta: {
                  showInMenu: false,
                  permission: permissionTypes.user.view,
                },
              },
              {
                path: 'detail/:id?',
                name: 'user-detail',
                component: () => import('@/modules/settings/user/UserDetailPage.vue'),
                meta: {
                  showInMenu: false,
                  permission: permissionTypes.user.view,
                },
              },
            ],
          },
          {
            path: 'feature-flags',
            name: 'feature-flags',
            component: () => import('@/modules/settings/feature-flag/FeatureFlagPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.featureFlags.view,
            },
          },
          {
            path: 'turbo-advertisement',
            name: 'turbo-advertisement',
            component: () => import('@/modules/settings/turbo-advertisement/TurboAdvertisementPage.vue'),
            meta: {
              showInMenu: true,
              permission: permissionTypes.turboAdvertisement.view,
            },
          },
        ],
      },
    ],
  },
];

const routeOptions = {
  routes: [
    {
      // The dashboard is the landing page: editors see how their site is doing before
      // anything else.
      path: '/',
      redirect: 'dashboard',
    },
    {
      path: '/switch-site/:id?',
      name: 'switch-site',
      meta: {
        isPublic: true,
      },
      component: () => import('@/modules/site/SwitchSitePage.vue'),
    },
    {
      path: '/',
      component: () => import('@/layouts/Base.vue'),
      children: [
        ...siteRoutes,
      ],
    },
    {
      path: '/',
      component: () => import('@/layouts/Error.vue'),
      children: [
        {
          path: '/unauthenticated',
          name: 'unauthenticated',
          meta: {
            isPublic: true,
          },
          component: () => import('@/modules/default/UnauthenticatedPage.vue'),
        },
        {
          path: '/unauthorized',
          name: 'unauthorized',
          meta: {
            isPublic: true,
          },
          component: () => import('@/modules/default/UnauthorizedPage.vue'),
        },
        {
          path: '/unauthorized-user',
          name: 'unauthorized-user',
          meta: {
            isPublic: true,
          },
          component: () => import('@/modules/default/UnauthorizedUserPage.vue'),
        },
        {
          path: '*',
          name: '404',
          meta: {
            isPublic: true,
          },
          component: () => import('@/modules/default/NotFoundPage.vue'),
        },
      ],
    },
  ],
};

const router = new Router(routeOptions);

const UNAUTHORIZED_PATH = '/unauthorized';

const isNotGrantedToViewPage = to => {
  const {
    permission, roles, isPublic, requiresSiteAccess,
  } = to.meta;

  if (isPublic) {
    return false;
  }

  const { selectedSiteId } = useSiteProvider();

  // Pages restricted to specific roles (e.g. Admin, Senior Editor) regardless of the
  // permission matrix; the remaining checks below still apply on top of this gate.
  if (Array.isArray(roles) && !hasAnyRole(roles, currentUserRoles.value)) {
    return true;
  }

  // Pages open to every member of a site — the only gate is the site itself, so a user
  // without any permission on the selected site is still turned away.
  if (requiresSiteAccess) {
    return !hasSiteAccess(selectedSiteId.value, currentUserRoles.value);
  }

  if (!permission) {
    return true;
  }

  return !(isGranted(selectedSiteId.value, permission, currentUserRoles.value));
};

router.beforeEach(async (to, from, next) => {
  const { $msal } = router.app.$root;

  const isAuthenticated = $msal.isAuthenticated();

  if (isAuthenticated) {
    setAuthUser($msal.data.user.accountIdentifier);
    await acquireIdToken($msal);
    await loadSites();
    await loadUser();
    await loadPermissions();

    if (to.meta?.redirectFirstChild) {
      const getPermittedFirstChild = children => {
        for (const child of children) {
          if (!isNotGrantedToViewPage(child)) {
            return child;
          }
        }
      }

      const checkFirstChild = async child => {
        if (child.meta.redirectFirstChild) {
          const firstChild = getPermittedFirstChild(child.children || []);

          if (firstChild) {
            await checkFirstChild(firstChild)
          }
        } else if (from.name !== child.name) {
          await router.push({ name: child.name });
        }
      };

      const firstChild = getPermittedFirstChild(findInNestedArray(siteRoutes, 'children', 'name', to.name)?.children || []);

      if (firstChild) {
        await checkFirstChild(firstChild);

        return;
      }
    }

    if (!router.app.initialized) {
      await Vue.nextTick();
    }

    if (to.name === 'content' && isNotGrantedToViewPage(to)) {
      await router.push({ name: 'archive-list' });

      return;
    }

    if (to.name === 'archive-list' && isNotGrantedToViewPage(to)) {
      await router.push({ name: 'agency-list' });

      return;
    }

    if (isNotGrantedToViewPage(to)) {
      await router.push(UNAUTHORIZED_PATH);

      return;
    }

    next();

    return;
  }

  $msal.signIn();
});

export default router;
